import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useDispatch } from "react-redux";

import A5Report from "@/containers/Dashboard/Providers/A5Report";
import A5ScreeningDetails from "@/components/Common/A5ScreeningDetails";
import A5ScreeningList from "@/components/Common/A5ScreeningList";
import AddAppointment from "@/components/Provider/ScheduleAvailability/AddAppointment";
import { admin_admin, admin_bp, admin_cse, adminRoute, baseRoute } from "@/config/roles";
import { AdminProfile } from "@/portals/admin/(profile)/AdminProfile";
import { AdvancedCareActivityNewNote } from "@/features/notes/(v2)/(advanced-care)/AdvancedCareActivityNewNote";
import { canCreateAdvancedCareActivityNotes } from "@/features/acp/helpers/note-creation";
import CareTeamList from "@/containers/Dashboard/Providers/PatientProfileView/CareTeamList";
import ChangePassword from "@/components/Authentication/ChangePassword";
import ClinicalAssessment from "@/containers/Dashboard/Providers/PatientProfileView/ClinicalAssessment";
import CustomerSuccessManagerList from "@/containers/Dashboard/Admin/CustomerSuccessManagerList";
import DischargeData from "@/components/Provider/PatientView/MemberProfile/DischargeData";
import { DraftNotes } from "@/portals/(shared)/(draft-notes)/DraftNotes";
import FileList from "@/containers/Dashboard/Providers/FileList";
import { Footer } from "@/component-library/Footer";
import GAD7 from "@/features/assessments/GAD7";
import { getAdminProfile } from "@/redux/actions/auth";
import IBARReport from "@/containers/Dashboard/Providers/IBARReport";
import InsuranceEligibilityQueueManagement from "@/components/Admin/InsuranceEligibilityQueueManagement/InsuranceEligibilityQueueManagement";
import InsuranceEligibilityRecordManagement from "@/components/Admin/InsuranceEligibilityRecordManagement";
import IPSRReport from "@/containers/Dashboard/Providers/IPSRReport";
import { isAuthorized } from "@/utils/authToken";
import { Main2 } from "@/components/layout/Main";
import mapLabelToComponent from "@/containers/Dashboard/common/mapLabelToComponent";
import { MemberProfile } from "@/features/member-profile/MemberProfile";
import NewPractice from "@/portals/admin/(practice)/NewPractice";
import { NewProvider } from "@/portals/admin/(practice)/NewProvider";
import NewReport from "@/containers/Dashboard/Providers/NewReport";
import { NoteDetailPage } from "@/features/notes/(legacy)/NoteDetailPage";
import { NoteViewV2 } from "@/features/notes/(v2)/NoteViewV2";
import OBARReport from "@/containers/Dashboard/Providers/OBARReport";
import OmnisearchList from "@/containers/Dashboard/Admin/OmnisearchList";
import OnBehalfOfSchedule from "@/containers/Dashboard/Providers/OnBehalfOfSchedule";
import OPSRReport from "@/containers/Dashboard/Providers/OPSRReport";
import Patient from "@/containers/Dashboard/Providers/Patient";
import PatientsList from "@/containers/Dashboard/Providers/PatientsList/PatientsList";
import PHQ9 from "@/features/assessments/PHQ9";
import Practice from "@/portals/admin/(practice)/Practice";
import PracticeList from "@/portals/admin/(practice)/PracticeList";
import { PrintNoteView } from "@/features/notes/(v2)/PrintNoteView";
import PrivacyPolicy from "@/components/Common/PrivacyPolicy";
import Provider from "@/containers/Dashboard/Admin/Provider";
import { ProviderProfile } from "@/portals/provider/(profile)/ProviderProfile";
import Ratings from "@/containers/Dashboard/Providers/PatientProfileView/Ratings";
import ReferralView from "@/containers/Dashboard/Providers/ReferralView";
import Reports from "@/containers/Dashboard/Providers/Reports";
import SchedulePageV2 from "@/containers/Dashboard/Providers/SchedulePageV2";
import Settings from "@/containers/Dashboard/common/SettingsPage";
import { Toolbar } from "@/components/Navigation/Toolbar/Toolbar";
import TutorialScreens from "@/components/Common/TutorialScreen";
import { useCosignSupervisor } from "@/features/cosignature/hooks/useCosignSupervisor";
import { WaitlistForm } from "@/components/Common/WaitlistForm/WaitlistForm";
import WorkingHours from "@/portals/(shared)/components/WorkingHours";

import NewNote from "../(shared)/(new-note)/NewNote";
import ProgressNoteList from "../(shared)/ProgressNoteList";
import { ViewDraftNote } from "../(shared)/(new-note)/DraftNoteView";
import ViewDraftNoteV2 from "../(shared)/(new-note)/DraftNoteViewV2";

import { ADMIN_BASE_REDIRECT_ROUTE } from "./constants";
import { Cosignature } from "./(cosignature)/Cosignature";

const DEFAULT_ROUTE_SECTION_OPTIONS = [
  {
    icon: "NavMenu__practices-icon",
    label: "Practices",
    mobileIcon: "practices.svg",
    route: adminRoute + "/practice",
  },
  {
    icon: "NavMenu__screening-icon",
    label: "Screening",
    mobileIcon: "screening.svg",
    route: adminRoute + "/a5_screenings",
  },
  {
    icon: "NavMenu__reports-icon",
    label: "Reports",
    mobileIcon: "reports.svg",
    route: adminRoute + "/reports",
  },
  {
    icon: "NavMenu__settings-icon",
    label: "Settings",
    mobileIcon: "settings.svg",
    route: adminRoute + "/settings",
  },
];

const ADMIN_OR_CSE_ROUTE_SECTION_OPTIONS = [
  {
    activeIconComponent: mapLabelToComponent("Practices", true),
    icon: "NavMenu__practices-icon",
    iconComponent: mapLabelToComponent("Practices", false),
    label: "Practices",
    mobileIcon: "practices.svg",
    route: adminRoute + "/practice",
  },
  {
    activeIconComponent: mapLabelToComponent("Screening", true),
    icon: "NavMenu__screening-icon",
    iconComponent: mapLabelToComponent("Screening", false),
    label: "Screening",
    mobileIcon: "screening.svg",
    route: adminRoute + "/a5_screenings",
  },
  {
    activeIconComponent: mapLabelToComponent("Customer Success", true),
    icon: "NavMenu__patients-icon",
    iconComponent: mapLabelToComponent("Customer Success", false),
    label: "Customer Success",
    mobileIcon: "customer_success.svg",
    route: adminRoute + "/customer_success",
  },
  {
    icon: "NavMenu__settings-icon",
    label: "Add New Practice",
    mobileIcon: "add_new.svg",
    mobileOnly: true,
    route: adminRoute + "/practice/add",
  },
  {
    activeIconComponent: mapLabelToComponent("Reports", true),
    icon: "NavMenu__reports-icon",
    iconComponent: mapLabelToComponent("Reports", false),
    label: "Reports",
    mobileIcon: "reports.svg",
    route: adminRoute + "/reports",
  },
  {
    activeIconComponent: mapLabelToComponent("Eligibility", true),
    icon: "NavMenu__eligibility-icon",
    iconComponent: mapLabelToComponent("Eligibility", false),
    label: "Eligibility",
    mobileIcon: "eligibility-icon-navy.svg",
    route: adminRoute + "/eligibility",
  },
  {
    iconComponent: mapLabelToComponent("Search", false),
    label: "Search",
    route: adminRoute + "/search",
  },
];

const BILLING_PARTNER_ROUTE_SECTION_OPTIONS = [
  {
    icon: "NavMenu__reports-icon",
    label: "Reports",
    mobileIcon: "reports.svg",
    route: adminRoute + "/reports",
  },
  {
    icon: "NavMenu__settings-icon",
    label: "Settings",
    mobileIcon: "settings.svg",
    route: adminRoute + "/settings",
  },
  {
    iconComponent: mapLabelToComponent("Search", false),
    label: "Search",
    route: adminRoute + "/search",
  },
];

const CO_SIGN_NAV_ITEM = {
  activeIconComponent: mapLabelToComponent("Co-Sign", true),
  icon: "NavMenu__cosign-icon",
  iconComponent: mapLabelToComponent("Co-Sign", false),
  label: "Co-Sign",
  route: adminRoute + "/cosignature",
};

export function AdminRouter() {
  const dispatch = useDispatch();

  const { isSupervisor } = useCosignSupervisor();

  const isCse = isAuthorized(admin_cse);
  const isAdminOrCse = isCse || isAuthorized(admin_admin);

  const routeSectionOptions = (() => {
    if (isAdminOrCse) {
      if (isSupervisor) {
        const options = [...ADMIN_OR_CSE_ROUTE_SECTION_OPTIONS];

        // insert after Screening (index 1)
        options.splice(2, 0, CO_SIGN_NAV_ITEM);

        return options;
      }

      return ADMIN_OR_CSE_ROUTE_SECTION_OPTIONS;
    }
    if (isAuthorized(admin_bp)) return BILLING_PARTNER_ROUTE_SECTION_OPTIONS;

    return DEFAULT_ROUTE_SECTION_OPTIONS;
  })();

  const navbarConfiguration = {
    customRenderSection: [],
    routeSection: { options: routeSectionOptions },
    selfSection: {},
  };

  React.useEffect(() => {
    dispatch(getAdminProfile());
  }, [dispatch]);

  return (
    <>
      <Toolbar
        baseRoute={baseRoute}
        dynamicRouteConfig={{}}
        enableSearch={true}
        isMessagingEnabled={false}
        navbarConfiguration={navbarConfiguration}
      />

      <Main2>
        <Switch>
          {isCse && (
            <Route
              component={WaitlistForm}
              path={adminRoute + "/patient/:patientId/waitlist_form"}
            />
          )}
          <Route
            path={adminRoute + "/settings"}
            render={() => <Settings baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/schedule/:providerId/view"}
            render={(props) => <SchedulePageV2 {...props} />}
          />
          <Route component={AddAppointment} path={adminRoute + "/schedule/:providerId/add"} />
          <Route component={OnBehalfOfSchedule} path={adminRoute + "/schedule/:providerId"} />
          <Route
            path={adminRoute + "/profile/hours/:providerId"}
            render={(props) => <WorkingHours {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/profile/:providerId"}
            render={(props) => <ProviderProfile {...props} baseRoute={adminRoute} />}
          />
          <Route component={AdminProfile} path={adminRoute + "/profile"} />
          <Route component={NewReport} path={adminRoute + "/reports/add"} />
          <Route
            path={adminRoute + "/reports"}
            render={(props) => <Reports {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/privacy-policy"}
            render={() => <PrivacyPolicy baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/tutorial"}
            render={() => <TutorialScreens baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/change-password"}
            render={(props) => <ChangePassword {...props} baseRoute={adminRoute} />}
          />
          <Route component={NewPractice} path={adminRoute + "/practice/add"} />
          <Route
            path={adminRoute + "/practice/:practiceId/provider/add"}
            render={(props) => <NewProvider {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/practice/:practiceId/provider/:providerId/patientsList"}
            render={(props) => (
              <PatientsList
                practiceId={props.match.practiceId}
                providerId={props.match.providerId}
                {...props}
                baseRoute={adminRoute}
              />
            )}
          />
          <Route
            path={adminRoute + "/practice/:practiceId/provider/:providerId"}
            render={(props) => <Provider {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/practice/:practiceId"}
            render={(props) => <Practice {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/practice"}
            render={(props) => <PracticeList {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/search"}
            render={(props) => <OmnisearchList {...props} baseRoute={adminRoute} />}
          />
          <Route
            path={adminRoute + "/patient/:patientId/file"}
            render={(props) => <FileList {...props} type="patient" />}
          />
          <Route component={NewNote} path={adminRoute + "/patient/:patientId/note/add"} />
          <Route
            component={ViewDraftNoteV2}
            path={adminRoute + "/patient/:patientId/note/:noteId/v2/draft"}
          />
          <Route
            component={ViewDraftNote}
            path={adminRoute + "/patient/:patientId/note/:noteId/draft"}
          />
          <Route component={PrintNoteView} path={adminRoute + "/patient/:patientId/export_notes"} />
          <Route component={NoteViewV2} path={adminRoute + "/patient/:patientId/note/:noteId/v2"} />
          <Route
            component={NoteDetailPage}
            path={adminRoute + "/patient/:patientId/note/:noteId"}
          />
          <Route component={ProgressNoteList} path={adminRoute + "/patient/:patientId/note"} />
          {canCreateAdvancedCareActivityNotes() && (
            <Route
              component={AdvancedCareActivityNewNote}
              path={adminRoute + "/patient/:patientId/advanced_care_activity"}
            />
          )}
          <Route
            component={ClinicalAssessment}
            path={adminRoute + "/patient/:patientId/clinical_assessment"}
          />
          <Route component={MemberProfile} path={adminRoute + "/patient/:patientId/profile/v2"} />
          <Route component={CareTeamList} path={adminRoute + "/patient/:patientId/care_team"} />
          <Route component={A5Report} path={adminRoute + "/patient/:patientId/report/a5"} />
          <Route component={IBARReport} path={adminRoute + "/patient/:patientId/report/ibar"} />
          <Route component={IPSRReport} path={adminRoute + "/patient/:patientId/report/ipsr"} />
          <Route component={OBARReport} path={adminRoute + "/patient/:patientId/report/obar"} />
          <Route component={OPSRReport} path={adminRoute + "/patient/:patientId/report/opsr"} />
          <Route component={Ratings} path={adminRoute + "/patient/:patientId/ratings"} />
          <Route component={PHQ9} path={adminRoute + "/patient/:patientId/phq-9"} />
          <Route component={GAD7} path={adminRoute + "/patient/:patientId/gad-7"} />
          <Route component={Reports} path={adminRoute + "/patient/:patientId/report"} />
          <Route
            component={DischargeData}
            path={adminRoute + "/patient/:patientId/discharge/:dischargeId"}
          />
          <Route component={Patient} path={adminRoute + "/patient/:patientId"} />
          {isAdminOrCse && (
            <Route component={CustomerSuccessManagerList} path={adminRoute + "/customer_success"} />
          )}
          <Route
            path={adminRoute + "/a5_screenings/:screening_id/file"}
            render={(props) => <FileList {...props} type="a5_screenings" />}
          />
          <Route
            component={A5ScreeningDetails}
            path={adminRoute + "/a5_screenings/:screening_id"}
          />
          <Route component={A5ScreeningList} path={adminRoute + "/a5_screenings"} />
          <Route
            path={adminRoute + "/referral/:referralCode/file"}
            render={(props) => <FileList {...props} type="referral" />}
          />
          <Route component={ReferralView} path={adminRoute + "/referral/:referralCode"} />
          <Route component={DraftNotes} path={adminRoute + "/draft-notes/:providerId"} />
          {isAdminOrCse && (
            <Route
              component={InsuranceEligibilityRecordManagement}
              path={adminRoute + "/eligibility/:id"}
            />
          )}
          {isAdminOrCse && (
            <Route
              component={InsuranceEligibilityQueueManagement}
              path={adminRoute + "/eligibility"}
            />
          )}
          <Route component={Cosignature} path={adminRoute + "/cosignature"} />
          <Redirect from={adminRoute} to={ADMIN_BASE_REDIRECT_ROUTE} />
        </Switch>
      </Main2>

      <Footer />
    </>
  );
}
